import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { Icon, Tab } from '@ibk/shared-ui';
import { PatFacilitatorModal } from '../../../../components/PatFacilitatorInfo';
import { NewRechargeStep, NewRechargeStepStatus } from '../../types';
import { mapEmissorProductToDisplay } from './emissorProductsMapper';
import styles from './Products.module.css';
const PAGE_SIZE = 6;
function getProductLabel(product) {
    return product.productName ?? product.productId;
}
function isInlineSvg(icon) {
    return icon.trimStart().startsWith('<svg');
}
function groupByCategory(products, categories) {
    return categories.map(category => ({
        ...category,
        items: products.filter(product => product.productCategoryCode === category.code),
    }));
}
function paginateItems(items, page) {
    const start = page * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
}
export function Products({ formData, onComplete, onReturn, selectVoucherType, selectVoucherTypeId, isLoading = false, headerStepper, products, categories, isEmissorFlow, }) {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);
    const [activeTabLabel, setActiveTabLabel] = useState(() => t('recharge.step.products.tab.all'));
    const [patModalProductName, setPatModalProductName] = useState(null);
    const patToastAnchorRef = useRef(null);
    const categoryGroups = useMemo(() => groupByCategory(products, categories), [products, categories]);
    const hasPreselected = useRef(false);
    useEffect(() => {
        setCurrentPage(0);
    }, [activeTabLabel]);
    useEffect(() => {
        if (hasPreselected.current)
            return;
        if (products.length === 0)
            return;
        const initialProduct = categoryGroups.find(group => group.items.length > 0)?.items[0] ?? products[0];
        if (!initialProduct)
            return;
        if (formData.voucherType) {
            const selectedProduct = products.find(product => getProductLabel(product) === formData.voucherType);
            const hasSelection = Boolean(selectedProduct) && formData.voucherTypeId === selectedProduct?.productId;
            if (hasSelection) {
                hasPreselected.current = true;
                return;
            }
        }
        hasPreselected.current = true;
        selectVoucherType(getProductLabel(initialProduct));
        if (initialProduct.productId) {
            selectVoucherTypeId(initialProduct.productId);
        }
    }, [categoryGroups, formData.voucherType, formData.voucherTypeId, products, selectVoucherType, selectVoucherTypeId]);
    const breadcrumbs = useMemo(() => [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ], [t]);
    const currentStepConfig = useMemo(() => ({
        id: NewRechargeStep.PRODUCTS,
        title: t('recharge.step.products.title'),
        number: 2,
        status: NewRechargeStepStatus.ACTIVE,
    }), [t]);
    const handleSelectProduct = useCallback((product) => {
        selectVoucherType(getProductLabel(product));
        if (product.productId) {
            selectVoucherTypeId(product.productId);
        }
    }, [selectVoucherType, selectVoucherTypeId]);
    const handleOpenPatModal = useCallback((productName) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        setPatModalProductName(productName);
    }, []);
    const renderBadge = (productIsPat) => {
        const key = productIsPat ? 'pat' : 'auxilio';
        const className = `${styles.badge} ${key === 'pat' ? styles.badgePat : styles.badgeAuxilio}`;
        return (_jsx("span", { className: className, "data-testid": `badgeProductFamily-${key}`, children: t(`recharge.step.products.family.${key}`) }));
    };
    const renderCard = (product) => {
        const label = getProductLabel(product);
        const isSelected = formData.voucherType === label;
        const cardClassName = `${styles.card} ${isSelected ? styles.cardSelected : ''}`.trim();
        const radioClassName = `${styles.radioCircle} ${isSelected ? styles.radioCircleSelected : ''}`.trim();
        const { icon, title, description } = mapEmissorProductToDisplay(product);
        const patProductName = title.toUpperCase().includes('PAT')
            ? title
            : `${title} ${t('recharge.step.products.family.pat')}`;
        const showPatInfoLink = isEmissorFlow && product.productIsPat;
        return (_jsxs("label", { className: cardClassName, children: [_jsx("input", { type: 'radio', name: 'newRechargeProductCard', value: label, checked: isSelected, onChange: () => handleSelectProduct(product), className: styles.visuallyHiddenInput, "data-testid": `buttonProductCard-${label}` }), _jsx("span", { className: radioClassName, "aria-hidden": 'true', "data-testid": `radioProductCard-${label}` }), renderBadge(product.productIsPat), _jsxs("span", { className: styles.cardBody, children: [_jsxs("span", { className: styles.cardHeader, children: [icon &&
                                    (isInlineSvg(icon) ? (_jsx("span", { className: styles.iconRemote, "aria-hidden": 'true', "data-testid": `iconProductCard-${label}`, dangerouslySetInnerHTML: { __html: icon } })) : (_jsx("img", { className: styles.iconRemote, src: icon, alt: '', "data-testid": `iconProductCard-${label}` }))), _jsx("span", { className: styles.title, children: title })] }), _jsxs("span", { className: styles.description, children: [description, showPatInfoLink && (_jsxs(_Fragment, { children: [' ', _jsx("button", { type: 'button', className: styles.patInfoLink, onClick: handleOpenPatModal(patProductName), "data-testid": `buttonProductCardPatInfo-${label}`, children: t('recharge.pat-facilitator.view') })] }))] })] })] }, product.productId || label));
    };
    const renderPagination = (items) => {
        if (items.length <= PAGE_SIZE) {
            return null;
        }
        const lastPageIndex = Math.ceil(items.length / PAGE_SIZE) - 1;
        const canGoPrev = currentPage > 0;
        const canGoNext = currentPage < lastPageIndex;
        return (_jsxs("div", { className: styles.paginationRow, "data-testid": 'paginationProducts', children: [_jsx("button", { type: 'button', className: `${styles.paginationButton} ${canGoPrev ? styles.paginationButtonActive : ''}`.trim(), onClick: () => setCurrentPage(page => Math.max(0, page - 1)), disabled: !canGoPrev, "data-testid": 'buttonPaginationPrev', "aria-label": 'Pagina anterior', children: _jsx(Icon, { name: 'arrowChevronLeft', variant: 'mask', tintColor: canGoPrev ? '#D64000' : '#CCCCCC', className: styles.paginationIcon, alt: '' }) }), _jsx("button", { type: 'button', className: `${styles.paginationButton} ${canGoNext ? styles.paginationButtonActive : ''}`.trim(), onClick: () => setCurrentPage(page => Math.min(lastPageIndex, page + 1)), disabled: !canGoNext, "data-testid": 'buttonPaginationNext', "aria-label": 'Proxima pagina', children: _jsx(Icon, { name: 'arrowChevronRight', variant: 'mask', tintColor: canGoNext ? '#D64000' : '#CCCCCC', className: styles.paginationIcon, alt: '' }) })] }));
    };
    const renderGrid = (items, testId) => {
        if (items.length === 0) {
            return (_jsx("div", { className: styles.emptyState, "data-testid": `${testId}-empty`, children: t('recharge.step.products.empty-state') }));
        }
        const paginatedItems = paginateItems(items, currentPage);
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.grid, "data-testid": testId, children: paginatedItems.map(renderCard) }), renderPagination(items)] }));
    };
    const tabItems = useMemo(() => [
        {
            label: t('recharge.step.products.tab.all'),
            children: renderGrid(products, 'gridProductsAll'),
        },
        ...categoryGroups.map(group => ({
            label: group.name,
            children: renderGrid(group.items, `gridProducts-${group.code}`),
        })),
    ], [products, categoryGroups, formData.voucherType, t, currentPage]);
    const isNextDisabled = isLoading || !formData.voucherType;
    return (_jsxs(ContentLayout, { breadcrumbs: breadcrumbs, sidebar: undefined, noMaxWidth: true, noBackground: true, ref: patToastAnchorRef, children: [_jsx("div", { className: styles.wizardCardShell, children: _jsxs(StepperCard, { currentStep: currentStepConfig, showStepper: false, actions: {
                        back: {
                            label: t('recharge.button.back'),
                            onClick: onReturn,
                        },
                        next: {
                            label: t('recharge.button.next'),
                            onClick: onComplete,
                            loading: isLoading,
                            disabled: isNextDisabled,
                        },
                    }, children: [headerStepper, _jsxs("div", { className: styles.content, "data-testid": 'viewNewRechargeProductsStepContent', children: [_jsx("p", { className: styles.helper, "data-testid": 'textNewRechargeProductsHelper', children: t('recharge.step.products.helper') }), _jsx(Tab, { items: tabItems, dataTestId: 'tabsNewRechargeProducts', className: styles.tab, defaultActiveTab: t('recharge.step.products.tab.all'), onTabChange: setActiveTabLabel })] })] }) }), _jsx(PatFacilitatorModal, { isOpen: isEmissorFlow && patModalProductName !== null, onClose: () => setPatModalProductName(null), productName: patModalProductName ?? '', anchorRef: patToastAnchorRef })] }));
}
